import {Page, PageProps} from "../../routing/types";
import {OTHER_HP_ROOT_SLUGS, PORTAL_HP} from "../../utility/navigationUtil";

export const largeNavHeight = '7.75rem'
export const smallNavHeight = '4.1rem'

export const sosSpecialTopicCategory = "SOSSpecialTopicCategory";
export const sosOffer = "SOSOffer";
export const sosSpecialTopic = "SOSSpecialTopic";
export const sosNews = "SOSNews";
export const sosPublication = "SOSPublication";
export const sosEvent = "SOSEvent";
export const sosPressrelease = "SOSPressrelease";
export const sosDonationProduct = "SOSDonationProduct";
export const sosDonationCertificate = "SOSDonationCertificate";
export const queryList = "CMQueryList";
export const cart = "warenkorb";

export const placement_header = "header";
export const placement_stageteaser = "stageteaser";
export const placement_footer = "footer";
export const placement_topnavi = "topnavi";

/**
 * Geteilte sx-Objekte für das Seitenraster der PageLayouts (Blank, EasyLang, Homepage, Preview).
 *
 * Es gibt bewusst je eine schlanke und eine volle Variante: die vollen Varianten setzen zusätzlich
 * `width: 100%` und die `!important`-Resets, die schlanken nicht. Die Layouts unterscheiden sich
 * genau darin, deshalb dürfen sie nicht zu einem Objekt zusammengefasst werden.
 */
export const pageHeaderSx = {
    position: 'sticky',
    top: 0,
    zIndex: 1200
} as const;

export const pageGridContainerSx = {
    alignItems: 'stretch',
    flexDirection: 'column',
    justifyContent: 'center',
    marginTop: '-4px'
} as const;

export const pageGridContainerFullWidthSx = {
    ...pageGridContainerSx,
    margin: '0!important',
    padding: '0!important',
    width: '100%'
} as const;

export const pageGridItemSx = {
    pt: '0!important'
} as const;

export const pageGridItemFullWidthSx = {
    ...pageGridItemSx,
    margin: '0!important',
    padding: '0!important',
    width: '100%'
} as const;

export const easyLangTopNaviStyle = {
    height: 52,
    position: "sticky",
    top: 0,
    zIndex: 1200,
    display: {
        xs: "none",
        md: "none",
        lg: "flex"
    },
    justifyContent: "space-between",
    width: '100%',
    flexGrow: 1,
    alignItems: "center",
    backgroundColor: "var(--background-white)"
};

export enum PageContentType {
    Cart = "Cart",
    Special = "Special",
    Standard = "Standard",
    Product = "Product"
}

export function getPageLayoutType(page: Page) {
    let layout = 'HomepageLayout';
    if (isBlankPage(page)) {
        layout = 'BlankPageLayout';
    }
    if (isEasyLangPage(page)) {
        layout = 'EasyLangLayout';
    }
    if (isWimmelbild(page)) {
        layout = 'BlankPageLayout';
    }
    return layout;
}

export function getPageContentType(contentType: string, currentURL?: string): PageContentType {
    if (isCart(currentURL)) {
        return PageContentType.Cart;
    }
    switch (contentType) {
        case sosEvent:
        case sosNews:
        case sosPressrelease:
        case sosPublication:
            return PageContentType.Special;
        default:
            return PageContentType.Standard;
    }
}

export function isProductPage(page: Page) {
    return page.type === sosDonationProduct;
}

export function isOfferPage(page: Page) {
    return page.type === sosOffer;
}

export function isCart(currentURL?: string) {
    return currentURL?.endsWith(cart)
}

export function isSpecialTopicPage(page: Page): boolean {
    return (page.__typename !== undefined &&
            (page.__typename == 'SOSSpecialTopicCategoryImpl' || page.__typename ==
                'SOSSpecialTopicImpl'))
        || (page.type !== undefined &&
            (page.type === sosSpecialTopicCategory || page!.type === sosSpecialTopic))

}

export function isPageType(page: Page | string) {
    return typeof page === "string" ?
        (page === "CMChannel" ||
            page === 'SOSWimmelbild' ||
            page === 'SOSOrganisation' ||
            page === sosOffer ||
            page == sosDonationProduct ||
            page == sosSpecialTopicCategory ||
            page == sosSpecialTopic)
        : (page.__typename != undefined && (
            page.__typename == 'CMChannelImpl' ||
            page.__typename == 'SOSOfferImpl' ||
            page.__typename == 'SOSOrganisationImpl' ||
            page.__typename == 'SOSSpecialTopicCategoryImpl' ||
            page.__typename == 'SOSSpecialTopicImpl'));
}

export function isPagelikeContent(page: any) {
    return typeof page === "string" ?
        getPageContentType(page) === PageContentType.Special
        : (page.__typename != undefined && (
            page.__typename == 'SOSEventImpl' ||
            page.__typename == 'SOSPublicationImpl' ||
            page.__typename == 'SOSNewsImpl' ||
            page.__typename == 'SOSPressreleaseImpl'
        ))
}

export function isEasyLangPage(fragment: Page) {
    return fragment?.grid?.name === 'SOS Leichte Sprache Seitenvorlage'
}

export function isNewsPage(page: Page) {
    return page.__typename != undefined &&
        (page.__typename === 'SOSNewsImpl' ||
            page.__typename === 'SOSPressreleaseImpl');
}

export function isPublicationPage(page: Page) {
    return page.__typename !== undefined &&
        (page.__typename === 'SOSPublicationImpl');
}

export function isDonationProduct(page: Page) {
    return page.__typename !== undefined &&
        (page.__typename === 'SOSDonationProductImpl');
}

export function isHome(selfurl: string) {
    return selfurl !== null && (selfurl === '/' || selfurl === '/' + PORTAL_HP);
}

export function isForeignHome(selfurl: string) {
    if (!selfurl) {
        return false;
    }
    return OTHER_HP_ROOT_SLUGS.includes(selfurl);
}

export function isOrganisation(page: Page) {
    return page.__typename !== undefined && page.__typename === 'SOSOrganisationImpl';
}

export function isBlankPage(fragment: Page) {
    return fragment.grid?.name === "SOS Blank Page"
}

export function isWimmelbild(fragment: Page) {
    return (fragment.type !== undefined && fragment.type === "SOSWimmelbild")
        || (fragment.__typename !== undefined && fragment.__typename == 'SOSWimmelbildImpl')
}

export function isPageWithHiddenNavi(page: Page) {
    return page.grid && page.grid.name === "SOS Seite ohne Navigation"
}

export function extractTaxonomies(props: PageProps): string[] {
    const taxonomies: string[] = [];
    if (props.page?.sosDonationPurposeTaxonomy?.[0]?.value) {
        taxonomies.push(props.page.sosDonationPurposeTaxonomy[0].value)
    }
    if (props.navigation?.content?.site?.root?.sosLocationTaxonomy?.[0]) {
        const location = props.navigation.content.site.root.sosLocationTaxonomy[0];
        taxonomies.push(location.shortName || location.value);
    }
    return taxonomies;
}